import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { purchaseOrdersAPI } from '../api/client';

const STATUS_COLOURS = {
  Draft:     'bg-gray-100 text-gray-700',
  Pending:   'bg-yellow-100 text-yellow-800',
  Approved:  'bg-green-100 text-green-700',
  Open:      'bg-blue-100 text-blue-700',
  Closed:    'bg-slate-100 text-slate-600',
  Cancelled: 'bg-red-100 text-red-700',
  Rejected:  'bg-red-100 text-red-700',
};

const APPROVAL_COLOURS = {
  Pending:  'bg-yellow-50 border-yellow-300 text-yellow-800',
  Waiting:  'bg-gray-50 border-gray-200 text-gray-500',
  Approved: 'bg-green-50 border-green-300 text-green-700',
  Rejected: 'bg-red-50 border-red-300 text-red-700',
  Cancelled:'bg-gray-50 border-gray-200 text-gray-400',
};

const fmtCurrency = (v, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(v ?? 0);

const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null); // { type: 'approve'|'reject'|'close', notes: '' }
  const [sendCC, setSendCC] = useState('');
  const [sendNote, setSendNote] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendMsg, setSendMsg] = useState(null); // { ok: bool, text: string }
  const [genLoading, setGenLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await purchaseOrdersAPI.get(id);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleAction = async (type) => {
    setActionLoading(true);
    try {
      if (type === 'submit')  await purchaseOrdersAPI.submit(id);
      if (type === 'approve') await purchaseOrdersAPI.approve(id, modal?.notes);
      if (type === 'reject')  await purchaseOrdersAPI.reject(id, modal?.notes);
      if (type === 'close')   await purchaseOrdersAPI.close(id);
      if (type === 'delete') {
        await purchaseOrdersAPI.delete(id);
        navigate('/purchase-orders');
        return;
      }
      setModal(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.message || `Failed to ${type} purchase order`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnmatch = async (matchId) => {
    if (!confirm('Remove this invoice match?')) return;
    try {
      await purchaseOrdersAPI.unmatchInvoice(id, matchId);
      await load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to remove match');
    }
  };

  const handleSendToVendor = async () => {
    setSendLoading(true);
    setSendMsg(null);
    try {
      await purchaseOrdersAPI.sendToVendor(id, { vendorEmailCC: sendCC.trim() || null, senderNote: sendNote.trim() || null });
      setSendMsg({ ok: true, text: 'Purchase order sent to vendor successfully.' });
      await load();
    } catch (e) {
      setSendMsg({ ok: false, text: e.response?.data?.message || 'Failed to send purchase order.' });
    } finally {
      setSendLoading(false);
    }
  };

  const handleGenerateNext = async () => {
    if (!confirm('Generate the next recurring PO as a new Draft?')) return;
    setGenLoading(true);
    try {
      const { data: d } = await purchaseOrdersAPI.generateNext(id);
      navigate(`/purchase-orders/${d.poId}`);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to generate next PO');
      setGenLoading(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (error)   return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!data)   return null;

  const { po, lines, approvals, matchedInvoices } = data;
  const remaining = (po.TotalAmount ?? 0) - (po.MatchedAmount ?? 0);
  const matchedPct = po.TotalAmount > 0 ? Math.min(100, ((po.MatchedAmount / po.TotalAmount) * 100)) : 0;

  const canSubmit  = po.Status === 'Draft';
  const canApprove = po.Status === 'Pending';
  const canClose   = ['Approved', 'Open'].includes(po.Status);
  const canDelete  = ['Draft', 'Rejected'].includes(po.Status);
  const canEdit    = po.Status === 'Draft';
  const canSend    = ['Approved', 'Open'].includes(po.Status) && !!po.VendorEmail;
  const canGenerateNext = ['Approved', 'Open'].includes(po.Status) && !!po.IsRecurring;

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button onClick={() => navigate('/purchase-orders')} className="text-gray-400 hover:text-gray-600 text-sm">← POs</button>
            <h1 className="text-2xl font-bold text-gray-900">{po.PONumber}</h1>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLOURS[po.Status] || 'bg-gray-100 text-gray-600'}`}>
              {po.Status}
            </span>
            {po.POType === 'blanket' && (
              <span className="bg-orange-50 text-orange-700 text-xs px-2 py-0.5 rounded-full font-semibold border border-orange-200">Blanket</span>
            )}
            {!!po.IsRecurring && (
              <span className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full font-semibold border border-teal-200">
                Recurring · {po.RecurringFrequency}
                {po.RecurringNextDate ? ` · Next: ${fmtDate(po.RecurringNextDate)}` : ''}
              </span>
            )}
            {po.Source !== 'internal' && (
              <span className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full font-semibold">{po.Source === 'dynamics_bc' ? 'Dynamics BC' : 'Xero'}</span>
            )}
          </div>
          <p className="text-gray-500 text-sm">{po.Description || 'No description'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit    && <button onClick={() => navigate(`/purchase-orders/${id}/edit`)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>}
          {canDelete  && <button onClick={() => handleAction('delete')} className="px-4 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50">Delete</button>}
          {canSubmit  && <button onClick={() => handleAction('submit')} disabled={actionLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Submit for Approval</button>}
          {canApprove && <button onClick={() => setModal({ type: 'approve', notes: '' })} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Approve</button>}
          {canApprove && <button onClick={() => setModal({ type: 'reject',  notes: '' })} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Reject</button>}
          {canClose   && <button onClick={() => handleAction('close')} disabled={actionLoading} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Close PO</button>}
          {canGenerateNext && (
            <button onClick={handleGenerateNext} disabled={genLoading} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {genLoading ? 'Generating…' : 'Generate Next PO'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Summary Card */}
        <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Summary</h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><dt className="text-gray-400">Vendor</dt><dd className="font-medium text-gray-800">{po.VendorName || '—'}</dd></div>
            <div><dt className="text-gray-400">Department</dt><dd className="font-medium text-gray-800">{po.DepartmentName || '—'}</dd></div>
            <div><dt className="text-gray-400">Currency</dt><dd className="font-medium text-gray-800">{po.Currency}</dd></div>
            <div><dt className="text-gray-400">PO Date</dt><dd className="font-medium text-gray-800">{fmtDate(po.PODate)}</dd></div>
            <div><dt className="text-gray-400">Expiry Date</dt><dd className="font-medium text-gray-800">{fmtDate(po.ExpiryDate)}</dd></div>
            <div><dt className="text-gray-400">Created by</dt><dd className="font-medium text-gray-800">{po.CreatedByName || po.CreatedByUsername || '—'}</dd></div>
            {po.ApprovedByName && <div><dt className="text-gray-400">Approved by</dt><dd className="font-medium text-green-700">{po.ApprovedByName}</dd></div>}
            {po.ApprovedAt && <div><dt className="text-gray-400">Approved at</dt><dd className="font-medium text-gray-800">{fmtDateTime(po.ApprovedAt)}</dd></div>}
            {po.ExternalRef && <div className="col-span-2"><dt className="text-gray-400">ERP Reference</dt><dd className="font-mono text-xs text-gray-600">{po.ExternalRef}</dd></div>}
            {po.Notes && <div className="col-span-2"><dt className="text-gray-400">Notes</dt><dd className="text-gray-700">{po.Notes}</dd></div>}
          </dl>
        </div>

        {/* Amounts Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Amounts</h2>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Total</span>
              <span className="font-bold text-gray-900">{fmtCurrency(po.TotalAmount, po.Currency)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Matched</span>
              <span className="font-medium text-blue-700">{fmtCurrency(po.MatchedAmount, po.Currency)}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Remaining</span>
              <span className={`font-bold ${remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtCurrency(remaining, po.Currency)}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${matchedPct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{matchedPct.toFixed(0)}% matched</p>
          </div>
          {po.CommittedAmount > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Budget Reserved</span>
                <span className="font-medium text-orange-700">{fmtCurrency(po.CommittedAmount, po.Currency)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white border border-gray-200 rounded-xl mb-4">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['#','Description','Qty','Unit Price','Total','GL Account','Budget Line'].map(h => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {lines.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-4 text-gray-400 text-center">No line items</td></tr>
              )}
              {lines.map(l => (
                <tr key={l.POLineID} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-400">{l.LineNumber}</td>
                  <td className="px-4 py-2 text-gray-800">{l.Description || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{l.Quantity}</td>
                  <td className="px-4 py-2 text-gray-600">{fmtCurrency(l.UnitPrice, po.Currency)}</td>
                  <td className="px-4 py-2 font-medium text-gray-800">{fmtCurrency(l.LineTotal, po.Currency)}</td>
                  <td className="px-4 py-2 text-gray-500">{l.AccountCode ? `${l.AccountCode} – ${l.AccountName}` : '—'}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {l.BudgetLineDescription
                      ? <span title={l.BudgetName || ''}>{l.BudgetLineDescription}{l.BudgetName ? <span className="text-xs text-gray-400 ml-1">({l.BudgetName})</span> : ''}</span>
                      : (l.BudgetName || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approval Chain */}
      {approvals.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl mb-4">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Approval Chain</h2>
          </div>
          <div className="p-5 space-y-2">
            {approvals.map((a, i) => (
              <div key={a.POApprovalID} className={`flex items-center gap-4 p-3 rounded-lg border ${APPROVAL_COLOURS[a.ApprovalStatus] || 'bg-gray-50'}`}>
                <span className="text-xs font-bold w-5 text-center">{i + 1}</span>
                <div className="flex-1">
                  <p className="font-medium text-sm">{a.ApproverName || a.ApproverUsername}</p>
                  {a.DepartmentName && <p className="text-xs opacity-70">{a.DepartmentName}</p>}
                </div>
                <span className="text-xs font-semibold">{a.ApprovalStatus}</span>
                {a.ActedAt && <span className="text-xs opacity-60">{fmtDateTime(a.ActedAt)}</span>}
                {a.Notes && <span className="text-xs italic opacity-70 max-w-xs truncate">{a.Notes}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matched Invoices */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Matched Invoices</h2>
        </div>
        {matchedInvoices.length === 0
          ? <p className="px-5 py-4 text-gray-400 text-sm">No invoices matched yet</p>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Invoice #','Amount','Status','Matched Amount','Matched By','Date',''].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {matchedInvoices.map(m => (
                  <tr key={m.MatchID} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <Link to={`/invoices/${m.InvoiceID}`} className="text-indigo-600 hover:underline font-medium">{m.InvoiceNumber}</Link>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{fmtCurrency(m.InvoiceAmount, po.Currency)}</td>
                    <td className="px-4 py-2"><span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{m.InvoiceStatus}</span></td>
                    <td className="px-4 py-2 font-medium text-blue-700">{fmtCurrency(m.MatchedAmount, po.Currency)}</td>
                    <td className="px-4 py-2 text-gray-500">{m.MatchedByUsername || '—'}</td>
                    <td className="px-4 py-2 text-gray-400 whitespace-nowrap">{fmtDateTime(m.MatchedAt)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleUnmatch(m.MatchID)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send to Vendor */}
      {canSend && (
        <div className="bg-white border border-gray-200 rounded-xl mt-4">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-800">Send to Vendor</h2>
              {po.SentToVendorAt && (
                <p className="text-xs text-gray-400 mt-0.5">Last sent: {fmtDateTime(po.SentToVendorAt)}</p>
              )}
            </div>
            {po.SentToVendorAt && (
              <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs px-2.5 py-1 rounded-full font-medium border border-green-200">
                ✓ Sent
              </span>
            )}
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Email</label>
              <input readOnly value={po.VendorEmail || ''} className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full bg-gray-50 text-gray-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CC (optional)</label>
              <input
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                value={sendCC}
                onChange={e => setSendCC(e.target.value)}
                placeholder={po.VendorEmailCC || 'e.g. finance@company.com, manager@company.com'}
              />
              <p className="text-xs text-gray-400 mt-1">Comma-separated. Leave blank to use saved CC list.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note to vendor (optional)</label>
              <textarea
                rows={2}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                value={sendNote}
                onChange={e => setSendNote(e.target.value)}
                placeholder="Any additional message to include in the email body…"
              />
            </div>
            {sendMsg && (
              <div className={`p-3 rounded-lg text-sm ${sendMsg.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {sendMsg.text}
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleSendToVendor}
                disabled={sendLoading}
                className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {sendLoading ? 'Sending…' : po.SentToVendorAt ? 'Resend PDF to Vendor' : 'Send PDF to Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve/Reject modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 capitalize">{modal.type} Purchase Order</h2>
            <p className="text-sm text-gray-600 mb-4">PO: <span className="font-medium">{po.PONumber}</span></p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={modal.notes}
              onChange={e => setModal(m => ({ ...m, notes: e.target.value }))}
              placeholder="Add any comments…"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button
                disabled={actionLoading}
                onClick={() => handleAction(modal.type)}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${modal.type === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {actionLoading ? 'Processing…' : `Confirm ${modal.type}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
